import { useEffect, useState } from "react";
import { BellRing, Check, CreditCard, ListChecks, MapPin, ScrollText } from "lucide-react";
import { api } from "../../lib/api";

// Carte « Relances et demandes d'adresses » de l'aperçu admin (demande du
// propriétaire, 2026-10-19) : pour chaque robot, combien de courriels sont
// partis et combien ont été CONFIRMÉS ensuite (adresse confirmée sur le
// portail, paiement reçu, épreuve répondue, soumission traitée).
const ROWS = [
  { key: "relance_adresse", label: "Relance adresse (après acceptation)", icon: MapPin,
    partis: "envoyees", suite: "confirmees", suiteLabel: "confirmées ensuite" },
  { key: "demande_adresses", label: "Demande d'adresses (bouton « SVP confirmer »)", icon: MapPin,
    partis: "envoyees", suite: "confirmees", suiteLabel: "confirmées ensuite" },
  { key: "rappel_paiement", label: "Rappel de paiement", icon: CreditCard,
    partis: "envoyes", suite: "payes", suiteLabel: "payées ensuite" },
  { key: "rappel_epreuve", label: "Rappel d'épreuve", icon: ScrollText,
    partis: "envoyes", suite: "repondues", suiteLabel: "répondues ensuite" },
  { key: "relance_soumission", label: "Relance soumission sans réponse (avis atelier)", icon: ListChecks,
    partis: "envoyees", suite: "traitees", suiteLabel: "traitées ensuite" },
];

export default function RelancesStats() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/reminder-stats")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;
  const totalPartis = ROWS.reduce((s, r) => s + (data[r.key]?.[r.partis] || 0), 0);
  const totalSuite = ROWS.reduce((s, r) => s + (data[r.key]?.[r.suite] || 0), 0);

  return (
    <div className="mt-8 bg-surface border border-gold/15 rounded-xl p-7" data-testid="relances-stats">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <BellRing className="w-4 h-4 text-gold" />
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">Relances et demandes d'adresses</h3>
        <span className="ml-auto text-xs text-ivory/55" data-testid="relances-stats-total">
          {totalPartis} partie{totalPartis > 1 ? "s" : ""} · {totalSuite} confirmée{totalSuite > 1 ? "s" : ""} ensuite
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-ivory/50 border-b border-ivory/10">
              <th className="pb-2 font-medium">Robot</th>
              <th className="pb-2 font-medium text-right">Parties</th>
              <th className="pb-2 font-medium text-right">Suite du client</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => {
              const partis = data[r.key]?.[r.partis] || 0;
              const suite = data[r.key]?.[r.suite] || 0;
              const pct = partis > 0 ? Math.round((suite / partis) * 100) : null;
              return (
                <tr key={r.key} className="border-b border-ivory/5 last:border-0" data-testid={`relances-row-${r.key}`}>
                  <td className="py-2.5 text-ivory/85">
                    <span className="inline-flex items-center gap-2">
                      <r.icon className="w-3.5 h-3.5 text-gold/70" /> {r.label}
                    </span>
                  </td>
                  <td className="py-2.5 text-right text-ivory/70" data-testid={`relances-parties-${r.key}`}>{partis}</td>
                  <td className="py-2.5 text-right" data-testid={`relances-suite-${r.key}`}>
                    <span className={`font-semibold ${suite > 0 ? "text-emerald-400" : "text-ivory/40"}`}>
                      {suite > 0 && <Check className="w-3 h-3 inline-block mr-1 -mt-0.5" />}
                      {suite} {r.suiteLabel}
                    </span>
                    {pct !== null && <span className="block text-[10px] text-ivory/40">{pct} %</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-ivory/55 mt-4">
        « Suite du client » compte les dossiers confirmés APRÈS l'envoi du courriel (adresse confirmée sur sa page,
        paiement reçu, épreuve répondue ou soumission traitée).
      </p>
    </div>
  );
}
